import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useDisclosure} from '@mantine/hooks';
import {IconAlertTriangle} from '@tabler/icons-react';
import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {
  UserGroupRequest,
  UserGroupResponse,
} from 'src/api/ProactApiClient';
import {
  EntityIdentifierEnum,
  ModuleEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SidebarLinkUserGroupsAdmin} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';
import {
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useGetUserGroupQuery,
  useUpdateUserGroupMutation,
} from 'src/redux/queries/userGroup';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2} from 'src/types/FormV2';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {UserGroupModulePermissionsForm} from './UserGroupModulePermissionsForm';
import {UserGroupObjectPermissionsForm} from './UserGroupObjectPermissionsForm';
import {UserGroupContentTab} from './sections/UserGroupContentTab';
import {
  CreateDefaultUserGroup,
  GetUserGroupPillConfig,
  MapFormDataToUserGroupModulePermissions,
  MapFormDataToUserGroupObjectPermissions,
  MapUserGroupModulePermissionResponseToFormData,
  MapUserGroupObjectPermissionResponseToFormData,
} from './userGroupHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Group',
  entityNamePlural: 'Groups',
  listPath: PathUtility.UserGroup(),
  viewPath: id => PathUtility.UserGroupView(String(id), 'content'),
};

export type UserGroupViewType =
  | 'content'
  | 'module-permissions'
  | 'object-permissions'
  | 'audit'
  | ':view'
  | '';

export interface UserGroupFormProps {
  id?: number;
  mode: 'create' | 'edit' | 'view';
}
export const UserGroupForm: React.FC<UserGroupFormProps> = ({id, mode}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const {view} = useParams();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const [isSavePending, setIsSavePending] = useState(false);
  const [item, setItem] = useState<UserGroupResponse | undefined>(undefined);
  const [formData, setFormData] = useState<IFormDataV2>({});
  const [modulePermissionFormData, setModulePermissionsFormData] =
    useState<IFormDataV2>({});
  const [objectPermissionFormData, setObjectPermissionsFormData] =
    useState<IFormDataV2>({});
  const validationFnRef = React.useRef<(() => boolean) | undefined>();

  const userGroupPerms = useModulePermissions(ModuleEnum.UserGroups);
  const auditPerms = useAuditLogPermissions();
  const extensionTabs = useExtensionTabs(ModuleEnum.UserGroups, id ?? 0);

  const {
    data: personnel,
    isFetching: isPersonnelFetching,
    isLoading: isPersonnelLoading,
  } = useGetPersonnelSelectOptionsQuery({
    start: 0,
    length: 10000,
    active: [UserStatusEnum.Active, UserStatusEnum.Pending],
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    superviserOnly: false,
  });

  const {
    data: userGroupData,
    isLoading: isUserGroupLoading,
    error: userGroupError,
  } = useGetUserGroupQuery(id!, {skip: id === undefined});

  const [createUserGroup] = useCreateUserGroupMutation();
  const [updateUserGroup] = useUpdateUserGroupMutation();
  const [deleteUserGroup] = useDeleteUserGroupMutation();

  const isDataLoading = isPersonnelFetching || isPersonnelLoading;

  const isLoading = isDataLoading || (id !== undefined && isUserGroupLoading);

  useEffect(() => {
    if (isDataLoading === true) {
      return;
    }

    if (mode === 'create') {
      setFormData(CreateDefaultUserGroup());
    }
  }, [mode, isDataLoading]);

  useEffect(() => {
    if (userGroupError) {
      setAlert(parseErrorMessage(userGroupError), 'error');
    }
  }, [userGroupError, setAlert]);

  useEffect(() => {
    if (id === undefined) {
      setItem(undefined);
      return;
    }

    if (userGroupData) {
      setItem(userGroupData);
      setFormData(userGroupData as IFormDataV2);
      setModulePermissionsFormData(
        MapUserGroupModulePermissionResponseToFormData(
          userGroupData?.modulePermissions
        )
      );
      setObjectPermissionsFormData(
        MapUserGroupObjectPermissionResponseToFormData(
          userGroupData?.objectPermissions
        )
      );
    }
  }, [id, userGroupData]);

  const handleSave = async () => {
    try {
      setIsSavePending(true);
      const body = {
        ...formData,
        targetUsers:
          formData.targetUsers?.map((i: ISelectOption) => i.id) ?? [],
        modulePermissions: MapFormDataToUserGroupModulePermissions(
          modulePermissionFormData
        ),
        objectPermissions: MapFormDataToUserGroupObjectPermissions(
          objectPermissionFormData
        ),
      } as UserGroupRequest;

      const res =
        id === undefined
          ? await createUserGroup(body).unwrap()
          : await updateUserGroup(body).unwrap();

      setAlert(
        id === undefined
          ? 'Group was successfully created'
          : 'Group was successfully updated',
        'success'
      );

      navigate(PathUtility.UserGroupView(res.id, view as UserGroupViewType), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleChange = React.useCallback(
    (name: string, value: any) => {
      setFormData(prev => ({...prev, [name]: value}));
    },
    [setFormData]
  );

  const handleModulePermissionChange = (name: string, value: any) => {
    setModulePermissionsFormData(prev => ({...prev, [name]: value}));
  };

  const handleObjectPermissionChange = (name: string, value: any) => {
    setObjectPermissionsFormData(prev => ({...prev, [name]: value}));
  };

  const handleValidateAndSave = () => {
    if (validationFnRef.current && validationFnRef.current()) {
      void handleSave();
    }
  };

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!id) {
        return;
      }

      setIsSavePending(true);

      await deleteUserGroup(id).unwrap();

      setAlert('Group was successfully archived', 'success');

      navigate(PathUtility.UserGroup());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const personnelOptions = (personnel?.data ?? []) as ISelectOption[];

  const contentTab = (
    <UserGroupContentTab
      formData={formData}
      onChange={handleChange}
      mode={mode}
      personnel={personnelOptions}
      validationFnRef={mode !== 'view' ? validationFnRef : undefined}
      isIdpManaged={item?.isIdpManaged === true}
    />
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<UserGroupViewType>({
    view: (view ?? '') as UserGroupViewType,
    defaultView: 'content',
    preloadTabs: true,
    tabsProps: {
      keepMounted: true,
    },
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: contentTab,
      },
      {
        view: 'module-permissions',
        label: 'Module Permissions',
        disabled: formData?.isPermissionGroup !== true || !formData?.name,
        component: (
          <UserGroupModulePermissionsForm
            mode={mode !== 'view' ? 'edit' : 'view'}
            formData={modulePermissionFormData}
            onChange={handleModulePermissionChange}
          />
        ),
      },
      {
        view: 'object-permissions',
        label: 'Object Permissions',
        disabled: formData?.isPermissionGroup !== true || !formData?.name,
        component: (
          <UserGroupObjectPermissionsForm
            mode={mode !== 'view' ? 'edit' : 'view'}
            formData={objectPermissionFormData}
            onChange={handleObjectPermissionChange}
          />
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        hidden: mode !== 'view',
        disabled: !auditPerms.view,
        component: item?.id ? (
          <AuditModule
            entityId={String(item.id)}
            entityType={EntityIdentifierEnum.UserGroup}
          />
        ) : null,
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      mode === 'create'
        ? navigate(PathUtility.UserGroupCreate(view as UserGroupViewType), {
            replace: true,
          })
        : mode === 'edit'
        ? navigate(
            PathUtility.UserGroupEdit(String(id), view as UserGroupViewType),
            {replace: true}
          )
        : navigate(
            PathUtility.UserGroupView(String(id), view as UserGroupViewType),
            {replace: true}
          ),
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: item?.name,
      }),
    [mode, id, item?.name]
  );

  const pills = useMemo(() => GetUserGroupPillConfig(item), [item]);

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.UserGroupView(String(id), view as UserGroupViewType)
      : PathUtility.UserGroup()
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = item ? item.name : 'New Group';

  const handleEdit = () =>
    navigate(PathUtility.UserGroupEdit(String(id), view as UserGroupViewType));

  return (
    <Stack gap="lg">
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkUserGroupsAdmin.icon}>
          {pageTitle}
        </PageHeader.Title>

        <PageHeader.Badges>
          {pills.map((pill, index) => (
            <PageHeader.Badge key={index} color={pill.color} variant="light">
              {pill.text}
            </PageHeader.Badge>
          ))}
          {isEditMode && (
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          )}
        </PageHeader.Badges>

        <PageHeader.Actions>
          {isViewMode && userGroupPerms.update && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}
          {isViewMode && userGroupPerms.delete && (
            <PageHeader.Action
              variant="delete"
              onClick={() => confirmDeleteHandlers.open()}
              loading={isSavePending}>
              Archive
            </PageHeader.Action>
          )}
          {!isViewMode && (
            <>
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant={isCreateMode ? 'create' : 'save'}
                onClick={handleValidateAndSave}
                loading={isSavePending}>
                {isCreateMode ? 'Create Group' : 'Save Changes'}
              </PageHeader.Action>
            </>
          )}
        </PageHeader.Actions>
      </PageHeader>

      {item?.active === false && (
        <Alert
          variant="light"
          color="yellow"
          title="Inactive Group"
          icon={<IconAlertTriangle />}>
          This group is inactive. It will not be used to send notifications or
          to perform other functions.
        </Alert>
      )}

      {RenderedTabsComponent}

      {!isViewMode && (
        <Group>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Group' : 'Save Changes'}
          </PageHeader.Action>
        </Group>
      )}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to archive this user group?"
        body="The user group will be archived and no longer be accessible. Any permissions or notification settings related to this rule will be disabled. Other items that target this group, such as Notification Rules or Task Assignments, are not deleted."
      />
    </Stack>
  );
};

export default UserGroupForm;
